import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { frFR } from '@mui/material/locale';

// Définition des couleurs personnalisées
const colors = {
  background: {
    gradient: 'linear-gradient(135deg, #3a4040, #4d6060)',
    light: '#4d6060',
    dark: '#3a4040',
  },
  text: {
    primary: '#d2d8d8',
    secondary: '#a0a9a9',
    light: '#ffffff',
  },
  table: {
    header: '#1e2424',
    headerText: '#d2d8d8',
    row: '#2d3a3a',
    rowText: '#d2d8d8',
    rowTextSecondary: '#a0a9a9',
    hover: 'rgba(72, 95, 95, 0.3)',
  },
  button: {
    primary: '#5e7672',
    primaryHover: '#4a5e5a',
    secondary: '#d17c5e',
    secondaryHover: '#b56a4f',
    success: '#6b9e76',
    successHover: '#5a8362',
    warning: '#d1a05e',
    warningHover: '#b38a4f',
    info: '#5e7672',
    infoHover: '#4a5e5a',
    text: '#ffffff',
    shadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
    shadowHover: '0 6px 8px rgba(0, 0, 0, 0.3)',
  },
  // Vous pouvez ajouter d'autres couleurs personnalisées ici
};

// Styles centralisés pour les boutons d'administration (Glassmorphism)
export const adminStyles = {
  glassButton: {
    bgcolor: 'rgba(0,0,0,0.6)',
    color: 'white',
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(255,255,255,0.2)',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'scale(1.1)',
    }
  }
};

// Création du thème de base
let theme = createTheme({
  palette: {
    primary: {
      main: '#5e7672',
      dark: '#4a5e5a',
      light: '#7a9490',
    },
    secondary: {
      main: '#d17c5e',
      dark: '#b56a4f',
      light: '#e09e85',
    },
    background: {
      default: colors.background.light,
      paper: '#4d6060',
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    error: {
      main: '#d17c5e',
    },
    warning: {
      main: '#d1a05e',
    },
    info: {
      main: '#5e7672',
    },
    success: {
      main: '#6b9e76',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 500,
      color: colors.text.primary,
      fontSize: '1.75rem',
      marginBottom: '0.5rem',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: `${colors.background.gradient}`,
          backgroundImage: `url('/images/filigrane.png')`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundSize: 'contain',
          backgroundAttachment: 'fixed',
          backgroundBlendMode: 'overlay',
          minHeight: '100vh',
          color: colors.text.primary,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.table.row,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          overflow: 'hidden',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.table.header,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.table.hover,
          },
        },
        head: {
          backgroundColor: colors.table.header,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          color: colors.table.rowText,
        },
        head: {
          color: colors.table.headerText,
          fontWeight: 'bold',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
          boxShadow: colors.button.shadow,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: colors.button.shadowHover,
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          '&.MuiButton-containedPrimary': {
            backgroundColor: colors.button.primary,
            color: colors.button.text,
            '&:hover': {
              backgroundColor: colors.button.primaryHover,
            },
          },
          '&.MuiButton-containedSecondary': {
            backgroundColor: colors.button.secondary,
            color: colors.button.text,
            '&:hover': {
              backgroundColor: colors.button.secondaryHover,
            },
          },
        },
        outlined: {
          borderWidth: 1,
          '&:hover': {
            borderWidth: 1,
          },
          '&.MuiButton-outlinedPrimary': {
            borderColor: colors.button.primary,
            color: colors.button.primary,
            '&:hover': {
              backgroundColor: 'rgba(52, 152, 219, 0.1)',
              borderColor: colors.button.primaryHover,
            },
          },
          '&.MuiButton-outlinedSecondary': {
            borderColor: colors.button.secondary,
            color: colors.button.secondary,
            '&:hover': {
              backgroundColor: 'rgba(231, 76, 60, 0.1)',
              borderColor: colors.button.secondaryHover,
            },
          },
        },
        text: {
          '&.MuiButton-textPrimary': {
            color: colors.button.primary,
            '&:hover': {
              backgroundColor: 'rgba(52, 152, 219, 0.1)',
            },
          },
          '&.MuiButton-textSecondary': {
            color: colors.button.secondary,
            '&:hover': {
              backgroundColor: 'rgba(231, 76, 60, 0.1)',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            transform: 'scale(1.1)',
          },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          boxShadow: colors.button.shadow,
          '& .MuiButton-root': {
            boxShadow: 'none',
          },
        },
      },
    },
    // Vous pouvez personnaliser d'autres composants MUI ici
  },
}, frFR); // Ajout de la localisation française

// Rendre les polices responsives
theme = responsiveFontSizes(theme);

// Exporter les couleurs personnalisées pour une utilisation en dehors du thème
export { colors };

export default theme;
